import cloneDeep from 'lodash/cloneDeep';
import React, {useState} from 'react';
import produce from 'immer';

import {useStagedState} from 'common/hooks/useStagedState';
import {MetricEntry, MetricEntryBehavior} from 'common/query-builder/query-entry-section/types';
import {DraggableEntry} from 'common/components/draggable-list';
import {BookmarkDragMetadata} from 'common/report/insights/contexts/drag-drop/types';
import {MetricType} from 'common/types/reports/bookmark';

import type {RowProps, UpdateEntryOpts} from '../types/catalog';
import {BehaviorEventRow, BehaviorProfileRow, BehaviorRowProps} from './event-row';
import {getInlineFiltersKey} from './event-row/utils';

/**
 * Renders a BehaviorEventRow with drop targets above and below it for
 * changing the order of behavior steps
 */
export function DraggableBehavior(
  props: BehaviorRowProps & {
    isLast?: boolean;
    disableDrag?: boolean;
    disableSortZones?: boolean;
    /** when defined, marks draggable items as a single index group */
    indexOverride?: number;
  },
) {
  const [stagedFilter, setStagedFilter] = useState<Obj | null>(null);

  const isUserBehavior = props.behavior.type === MetricType.Cohort || props.behavior.type === MetricType.People;

  /**
   * When the user is dragging a filter, we want to allow dropping on an entire sub-behavior only
   * when filters come from different metrics, otherwise the user is rearranging filters
   */
  const canAcceptFilter = (data: BookmarkDragMetadata) => {
    return getInlineFiltersKey(props) !== data.group;
  };

  const BehaviorRow = isUserBehavior ? BehaviorProfileRow : BehaviorEventRow;

  return (
    <DraggableEntry<BookmarkDragMetadata>
      useDropRef
      disabled={props.disableDrag}
      disableSortZones={props.disableSortZones}
      isLast={props.isLast ?? false}
      data={{
        type: `behavior-event`,
        index: props.indexOverride ?? props.index,
        group: props.entry._idx!,
        metricIndex: props.metricIndex,
        behaviorIndex: props.behaviorIndex ?? props.index,
        parentMetricIndex: props.parentMetricIndex,
        setStagedEntry: (filter) => setStagedFilter(filter),
      }}
      accepts={{
        /**
         * @todo(metrics) we need to implement more conditions here to disallow dropping incompatible filters across metrics
         * This issue exists in production as well
         */
        'global-filter': canAcceptFilter,
        'inline-filter': canAcceptFilter,
        breakdown: () => true,
      }}
      sortAccepts={{
        'behavior-event': () => true,
      }}
    >
      <BehaviorRow
        {...props}
        behaviorIndex={props.behaviorIndex ?? props.index}
        stagedFilter={stagedFilter}
        abortStagedFilter={() => setStagedFilter(null)}
      />
    </DraggableEntry>
  );
}

export function useRawBehaviorsList(
  behaviorsProp: MetricEntryBehavior[],
  onUpdate: (behaviors: MetricEntryBehavior[], opts?: UpdateEntryOpts) => void,
) {
  const [behaviors, setBehaviors] = useStagedState<MetricEntryBehavior[], UpdateEntryOpts>(
    behaviorsProp,
    (state, opts) => onUpdate(state, opts),
  );

  const handleAddBehavior = (newBehavior: MetricEntryBehavior, opts?: UpdateEntryOpts) => {
    setBehaviors([...behaviors, newBehavior], true, opts);
  };

  const handleUpdateBehavior = (index: number, updatedBehavior: MetricEntryBehavior, opts?: UpdateEntryOpts) => {
    setBehaviors(
      produce(behaviors, (draft) => {
        draft[index] = updatedBehavior;
      }),
      true,
      opts,
    );
  };

  const handleDeleteBehavior = (index: number, opts?: UpdateEntryOpts) => {
    setBehaviors(
      produce(behaviors, (draft) => {
        draft.splice(index, 1);
      }),
      true,
      opts,
    );
  };

  const handleDuplicateBehavior = (index: number, opts?: UpdateEntryOpts) => {
    setBehaviors(
      produce(behaviors, (draft) => {
        const behavior = {
          ...cloneDeep(draft[index]),
          renamed: undefined,
        };
        draft.splice(index + 1, 0, behavior);
      }),
      true,
      opts,
    );
  };

  return {
    behaviors,
    setBehaviors,
    handleAddBehavior,
    handleUpdateBehavior,
    handleDeleteBehavior,
    handleDuplicateBehavior,
  };
}

/**
 * Hook to manage a list of behaviors for a MetricEntry.
 */
export function useBehaviorsList(entry: MetricEntry, onUpdate: RowProps[`updateEntry`]) {
  return useRawBehaviorsList(entry.behavior.behaviors ?? [], (behaviors, opts) =>
    onUpdate(
      produce(entry, (draft) => {
        draft.behavior.behaviors = behaviors;
      }),
      opts,
    ),
  );
}
